/**
 * Currency normalization utility that converts currency symbols to ISO 4217 codes
 */

// Mapping of currency symbols and common representations to ISO 4217 codes
const CURRENCY_SYMBOL_TO_ISO: Record<string, string> = {
    // Dollar signs
    $: 'USD',
    USD: 'USD',
    US$: 'USD',
    'U.S.$': 'USD',
    'US Dollar': 'USD',
    'US Dollars': 'USD',
    'American Dollar': 'USD',
    'American Dollars': 'USD',

    // Euro
    '€': 'EUR',
    EUR: 'EUR',
    Euro: 'EUR',
    Euros: 'EUR',
    'European Euro': 'EUR',

    // British Pound
    '£': 'GBP',
    GBP: 'GBP',
    'British Pound': 'GBP',
    'Pound Sterling': 'GBP',
    Sterling: 'GBP',

    // Japanese Yen (note: ¥ is shared with Chinese Yuan, but context matters)
    JPY: 'JPY',
    Yen: 'JPY',
    'Japanese Yen': 'JPY',

    // Canadian Dollar
    C$: 'CAD',
    CAD: 'CAD',
    'Canadian Dollar': 'CAD',
    'Canadian Dollars': 'CAD',

    // Australian Dollar
    A$: 'AUD',
    AUD: 'AUD',
    'Australian Dollar': 'AUD',
    'Australian Dollars': 'AUD',

    // Swiss Franc
    CHF: 'CHF',
    'Swiss Franc': 'CHF',
    'Swiss Francs': 'CHF',

    // Swedish Krona
    SEK: 'SEK',
    'Swedish Krona': 'SEK',
    'Swedish Kronor': 'SEK',

    // Norwegian Krone
    NOK: 'NOK',
    'Norwegian Krone': 'NOK',
    'Norwegian Kroner': 'NOK',

    // Danish Krone
    DKK: 'DKK',
    'Danish Krone': 'DKK',
    'Danish Kroner': 'DKK',

    // Polish Złoty
    zł: 'PLN',
    PLN: 'PLN',
    'Polish Złoty': 'PLN',
    'Polish Zloty': 'PLN',

    // Czech Koruna
    Kč: 'CZK',
    CZK: 'CZK',
    'Czech Koruna': 'CZK',
    'Czech Crown': 'CZK',

    // Hungarian Forint
    Ft: 'HUF',
    HUF: 'HUF',
    'Hungarian Forint': 'HUF',

    // Romanian Leu
    lei: 'RON',
    RON: 'RON',
    'Romanian Leu': 'RON',

    // Bulgarian Lev
    лв: 'BGN',
    BGN: 'BGN',
    'Bulgarian Lev': 'BGN',

    // Croatian Kuna
    kn: 'HRK',
    HRK: 'HRK',
    'Croatian Kuna': 'HRK',

    // Russian Ruble
    '₽': 'RUB',
    RUB: 'RUB',
    'Russian Ruble': 'RUB',
    'Russian Rouble': 'RUB',

    // Ukrainian Hryvnia
    '₴': 'UAH',
    UAH: 'UAH',
    'Ukrainian Hryvnia': 'UAH',

    // Turkish Lira
    '₺': 'TRY',
    TRY: 'TRY',
    'Turkish Lira': 'TRY',
    'Turkish Lire': 'TRY',

    // Israeli Shekel
    '₪': 'ILS',
    ILS: 'ILS',
    'Israeli Shekel': 'ILS',
    'Israeli Shekels': 'ILS',

    // South African Rand
    R: 'ZAR',
    ZAR: 'ZAR',
    'South African Rand': 'ZAR',

    // Indian Rupee
    '₹': 'INR',
    INR: 'INR',
    'Indian Rupee': 'INR',
    'Indian Rupees': 'INR',

    // Pakistani Rupee
    '₨': 'PKR',
    PKR: 'PKR',
    'Pakistani Rupee': 'PKR',

    // Sri Lankan Rupee
    Rs: 'LKR',
    LKR: 'LKR',
    'Sri Lankan Rupee': 'LKR',

    // Bangladeshi Taka
    '৳': 'BDT',
    BDT: 'BDT',
    'Bangladeshi Taka': 'BDT',

    // Nepali Rupee
    रू: 'NPR',
    NPR: 'NPR',
    'Nepali Rupee': 'NPR',

    // Bhutanese Ngultrum
    'Nu.': 'BTN',
    BTN: 'BTN',
    'Bhutanese Ngultrum': 'BTN',

    // Chinese Yuan/Renminbi
    '¥': 'CNY',
    CNY: 'CNY',
    'Chinese Yuan': 'CNY',
    Renminbi: 'CNY',

    // Hong Kong Dollar
    HK$: 'HKD',
    HKD: 'HKD',
    'Hong Kong Dollar': 'HKD',

    // Singapore Dollar
    S$: 'SGD',
    SGD: 'SGD',
    'Singapore Dollar': 'SGD',

    // New Zealand Dollar
    NZ$: 'NZD',
    NZD: 'NZD',
    'New Zealand Dollar': 'NZD',

    // South Korean Won
    '₩': 'KRW',
    KRW: 'KRW',
    'South Korean Won': 'KRW',
    'Korean Won': 'KRW',

    // Thai Baht
    '฿': 'THB',
    THB: 'THB',
    'Thai Baht': 'THB',

    // Malaysian Ringgit
    RM: 'MYR',
    MYR: 'MYR',
    'Malaysian Ringgit': 'MYR',

    // Indonesian Rupiah
    Rp: 'IDR',
    IDR: 'IDR',
    'Indonesian Rupiah': 'IDR',

    // Philippine Peso
    '₱': 'PHP',
    PHP: 'PHP',
    'Philippine Peso': 'PHP',

    // Vietnamese Dong
    '₫': 'VND',
    VND: 'VND',
    'Vietnamese Dong': 'VND',

    // Brazilian Real
    R$: 'BRL',
    BRL: 'BRL',
    'Brazilian Real': 'BRL',

    // Mexican Peso
    MX$: 'MXN',
    MXN: 'MXN',
    'Mexican Peso': 'MXN',

    // Colombian Peso
    COL$: 'COP',
    COP: 'COP',
    'Colombian Peso': 'COP',

    // Chilean Peso
    CLP$: 'CLP',
    CLP: 'CLP',
    'Chilean Peso': 'CLP',

    // Peruvian Sol
    'S/': 'PEN',
    PEN: 'PEN',
    'Peruvian Sol': 'PEN',

    // Argentine Peso
    ARS$: 'ARS',
    ARS: 'ARS',
    'Argentine Peso': 'ARS',

    // Egyptian Pound
    'E£': 'EGP',
    EGP: 'EGP',
    'Egyptian Pound': 'EGP',

    // Moroccan Dirham
    DH: 'MAD',
    MAD: 'MAD',
    'Moroccan Dirham': 'MAD',

    // Tunisian Dinar
    DT: 'TND',
    TND: 'TND',
    'Tunisian Dinar': 'TND',

    // Algerian Dinar
    DA: 'DZD',
    DZD: 'DZD',
    'Algerian Dinar': 'DZD',

    // Saudi Riyal
    '﷼': 'SAR',
    SAR: 'SAR',
    'Saudi Riyal': 'SAR',

    // UAE Dirham
    AED: 'AED',
    'UAE Dirham': 'AED',

    // Kuwaiti Dinar
    KWD: 'KWD',
    'Kuwaiti Dinar': 'KWD',

    // Bahraini Dinar
    BHD: 'BHD',
    'Bahraini Dinar': 'BHD',

    // Qatari Riyal
    QAR: 'QAR',
    'Qatari Riyal': 'QAR',

    // Omani Rial
    OMR: 'OMR',
    'Omani Rial': 'OMR',

    // Jordanian Dinar
    JOD: 'JOD',
    'Jordanian Dinar': 'JOD',

    // Lebanese Pound
    'ل.ل': 'LBP',
    LBP: 'LBP',
    'Lebanese Pound': 'LBP',
}

/**
 * Normalizes a currency input (symbol, code, or name) to its ISO 4217 code
 * @param currency - The currency symbol, code, or name to normalize
 * @returns The ISO 4217 currency code, or null if not found
 */
export function normalizeCurrency(currency: string | null): string | null {
    if (!currency) {
        return null
    }

    // Trim whitespace and convert to uppercase for consistent matching
    const normalizedInput = currency.trim().toUpperCase()

    // If after trimming it's empty, return null
    if (!normalizedInput) {
        return null
    }

    // Direct lookup in our mapping
    if (CURRENCY_SYMBOL_TO_ISO[normalizedInput]) {
        return CURRENCY_SYMBOL_TO_ISO[normalizedInput]
    }

    // Also try the original input (preserving case) in case of exact matches
    if (CURRENCY_SYMBOL_TO_ISO[currency]) {
        return CURRENCY_SYMBOL_TO_ISO[currency]
    }

    // Handle common variations and misspellings
    const variations: Record<string, string> = {
        DOLLAR: 'USD',
        DOLLARS: 'USD',
        EUROS: 'EUR',
        POUND: 'GBP',
        POUNDS: 'GBP',
        STERLING: 'GBP',
        YENS: 'JPY',
        FRANC: 'CHF',
        FRANCS: 'CHF',
        KRONA: 'SEK',
        KRONOR: 'SEK',
        KRONE: 'NOK',
        KRONER: 'NOK',
        CROWN: 'CZK',
        CROWNS: 'CZK',
        FORINT: 'HUF',
        LEU: 'RON',
        LEV: 'BGN',
        KUNA: 'HRK',
        RUBLE: 'RUB',
        ROUBLE: 'RUB',
        HRYVNIA: 'UAH',
        LIRA: 'TRY',
        LIRE: 'TRY',
        SHEKEL: 'ILS',
        SHEKELS: 'ILS',
        RAND: 'ZAR',
        RUPEE: 'INR',
        RUPEES: 'INR',
        TAKA: 'BDT',
        NGULTRUM: 'BTN',
        YUAN: 'CNY',
        WON: 'KRW',
        BAHT: 'THB',
        RINGGIT: 'MYR',
        RUPIAH: 'IDR',
        PESO: 'PHP',
        PESOS: 'PHP',
        DONG: 'VND',
        REAL: 'BRL',
        REALS: 'BRL',
        DIRHAM: 'AED',
        DIRHAMS: 'AED',
        DINAR: 'KWD',
        DINARS: 'KWD',
        RIYAL: 'SAR',
        RIYALS: 'SAR',
        RIAL: 'OMR',
        RIALS: 'OMR',
    }

    if (variations[normalizedInput]) {
        return variations[normalizedInput]
    }

    // If we can't find a match, return the original input as-is
    // This allows for already-correct ISO codes to pass through
    return currency
}

// Mapping of ISO 4217 codes to their primary display symbols
export const ISO_TO_CURRENCY_SYMBOL: Record<string, string> = {
    // Major currencies with their primary symbols
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'CHF',
    SEK: 'SEK',
    NOK: 'NOK',
    DKK: 'DKK',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    HRK: 'kn',
    RUS: '₽',
    RUB: '₽',
    UAH: '₴',
    TRY: '₺',
    ILS: '₪',
    ZAR: 'R',
    INR: '₹',
    PKR: '₨',
    LKR: 'Rs',
    BDT: '৳',
    NPR: 'रू',
    BTN: 'Nu.',
    CNY: '¥',
    HKD: 'HK$',
    SGD: 'S$',
    NZD: 'NZ$',
    KRW: '₩',
    THB: '฿',
    MYR: 'RM',
    IDR: 'Rp',
    PHP: '₱',
    VND: '₫',
    BRL: 'R$',
    MXN: 'MX$',
    COP: 'COL$',
    CLP: 'CLP$',
    PEN: 'S/',
    ARS: 'ARS$',
    EGP: 'E£',
    MAD: 'DH',
    TND: 'DT',
    DZD: 'DA',
    SAR: '﷼',
    AED: 'AED',
    KWD: 'KWD',
    BHD: 'BHD',
    QAR: 'QAR',
    OMR: 'OMR',
    JOD: 'JOD',
    LBP: 'ل.ل',
}

/**
 * Converts an ISO 4217 currency code to its display symbol
 * @param isoCode - The ISO 4217 currency code to convert
 * @returns The currency symbol for display, or the ISO code if no symbol is found
 */
export function getCurrencySymbol(isoCode: string | null): string {
    if (!isoCode) {
        return ''
    }

    const normalizedCode = isoCode.trim().toUpperCase()

    // If after trimming it's empty, return empty string
    if (!normalizedCode) {
        return ''
    }

    // Return the symbol if we have it mapped
    if (ISO_TO_CURRENCY_SYMBOL[normalizedCode]) {
        return ISO_TO_CURRENCY_SYMBOL[normalizedCode]
    }

    // If no symbol found, return the ISO code as fallback
    console.warn(
        `No display symbol found for currency: ${isoCode}. Using ISO code.`,
    )
    return isoCode
}

/**
 * Formats an amount with its currency symbol for display
 * @param amount - The numeric amount
 * @param isoCode - The ISO 4217 currency code
 * @param locale - Optional locale for number formatting (defaults to 'en-US')
 * @returns Formatted currency string like "$1,234.56" or "€1.234,56"
 */
export function formatCurrencyForDisplay(
    amount: number,
    isoCode: string | null,
    locale: string = 'en-US',
): string {
    if (amount == null || !isoCode) {
        return ''
    }

    const symbol = getCurrencySymbol(isoCode)

    try {
        // Use Intl.NumberFormat for proper locale-aware formatting
        const formatter = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })

        const formattedAmount = formatter.format(amount)

        // Position the symbol based on common conventions
        // Most currencies put symbol before amount, some after
        const symbolAfterCurrencies = [
            'RUB',
            'UAH',
            'TRY',
            'ILS',
            'INR',
            'PKR',
            'LKR',
            'NPR',
            'THB',
            'KRW',
        ]

        if (symbolAfterCurrencies.includes(isoCode.toUpperCase())) {
            return `${formattedAmount}${symbol}`
        } else {
            return `${symbol}${formattedAmount}`
        }
    } catch (error) {
        // Fallback to basic formatting if Intl fails
        console.warn(`Error formatting currency: ${error}`)
        return `${symbol}${amount.toFixed(2)}`
    }
}

/**
 * Checks if a string is a valid ISO 4217 currency code
 * @param currency - The currency code to validate
 * @returns true if it's a valid ISO 4217 code, false otherwise
 */
export function isValidISOCurrencyCode(currency: string | null): boolean {
    if (!currency) {
        return false
    }

    // ISO 4217 codes are exactly 3 letters, all uppercase
    return /^[A-Z]{3}$/.test(currency.trim())
}
